import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api, { formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  EnhancedTable,
  StickyTableHeader,
  SortableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
  SavedFiltersDropdown,
} from '../components/ui/enhanced-table';
import { useTableSort, useSavedFilters } from '../hooks/useTableEnhancements';
import { Plus, Pencil, Trash2, Users as UsersIcon, Search, X, Key } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = ['admin', 'supervisor', 'branch_manager', 'technician', 'requester'];

export default function UsersPage() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', username: '', password: '', role: 'requester', allowed_branch_ids: [] });

  // Filters
  const [fRole, setFRole] = useState('all');
  const [fBranch, setFBranch] = useState('all');
  const [fSearch, setFSearch] = useState('');

  // Sorting with persistence
  const { sortField, sortDir, toggleSort, sortData } = useTableSort('users_page', 'name', 'asc');

  // Saved filters
  const { savedFilters, saveCurrentFilters, saveFilterPreset, deleteFilterPreset, loadCurrentFilters } = useSavedFilters('users_page');

  // Load saved filters on mount
  useEffect(() => {
    const saved = loadCurrentFilters();
    if (saved) {
      if (saved.role) setFRole(saved.role);
      if (saved.branch) setFBranch(saved.branch);
    }
  }, []);

  // Save current filters when they change
  useEffect(() => {
    saveCurrentFilters({ role: fRole, branch: fBranch });
  }, [fRole, fBranch, saveCurrentFilters]);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [userRes, branchRes] = await Promise.all([api.get('/users'), api.get('/branches')]);
      setUsers(userRes.data);
      setBranches(branchRes.data);
    } catch (err) { toast.error(t('toast.error')); }
    finally { setLoading(false); }
  };

  // Filtered and sorted users
  const filteredUsers = useMemo(() => {
    let result = users;
    if (fRole !== 'all') {
      result = result.filter(u => u.role === fRole);
    }
    if (fBranch !== 'all') {
      result = result.filter(u => u.allowed_branch_ids?.includes(fBranch));
    }
    if (fSearch) {
      const q = fSearch.toLowerCase();
      result = result.filter(u => 
        u.name.toLowerCase().includes(q) || 
        u.email.toLowerCase().includes(q)
      );
    }
    return sortData(result);
  }, [users, fRole, fBranch, fSearch, sortData]);

  const toggleBranch = (branchId) => {
    setForm(f => ({
      ...f,
      allowed_branch_ids: f.allowed_branch_ids.includes(branchId)
        ? f.allowed_branch_ids.filter(id => id !== branchId)
        : [...f.allowed_branch_ids, branchId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role, allowed_branch_ids: form.allowed_branch_ids };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success(t('toast.saved'));
      } else {
        const payload = { ...form };
        if (!payload.username) delete payload.username;
        await api.post('/users', payload);
        toast.success(t('toast.saved'));
      }
      setDialogOpen(false);
      setEditing(null);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('toast.error')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('users.confirmDelete'))) return;
    try { await api.delete(`/users/${id}`); toast.success(t('toast.deleted')); loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || t('toast.error')); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, allowed_branch_ids: u.allowed_branch_ids || [] });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', username: '', password: '', role: 'requester', allowed_branch_ids: [] });
    setDialogOpen(true);
  };

  const roleLabel = (r) => ({ 
    admin: t('users.admin'), 
    supervisor: t('users.supervisor'), 
    branch_manager: t('users.branch_manager'),
    technician: t('users.technician'), 
    requester: t('users.requester') 
  }[r] || r);

  const roleStyles = (r) => ({
    admin: 'bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-700 dark:border-zinc-700',
    supervisor: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
    branch_manager: 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800',
    technician: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
    requester: 'bg-muted text-muted-foreground border-border'
  }[r] || 'bg-muted text-muted-foreground border-border');

  const clearFilters = () => {
    setFRole('all');
    setFBranch('all');
    setFSearch('');
  };

  const applyFilterPreset = (filters) => {
    if (filters.role) setFRole(filters.role);
    if (filters.branch) setFBranch(filters.branch);
    toast.success(t('filters.applied'));
  };

  const hasActiveFilters = fRole !== 'all' || fBranch !== 'all' || fSearch;

  return (
    <div data-testid="users-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{t('users.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{filteredUsers.length} {t('users.title').toLowerCase()}</p>
        </div>
        <div className="flex gap-2">
          <SavedFiltersDropdown
            savedFilters={savedFilters}
            onSave={saveFilterPreset}
            onApply={applyFilterPreset}
            onDelete={deleteFilterPreset}
            currentFilters={{ role: fRole, branch: fBranch }}
            t={t}
          />
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="create-user-btn">
                <Plus className="w-4 h-4 me-2" /> {t('users.addUser')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-barlow text-xl">{editing ? t('users.editUser') : t('users.createUser')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
                <div className="space-y-2">
                  <Label>{t('common.name')} *</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>{t('users.email')} *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" />
                </div>
                {!editing && (
                  <div className="space-y-2">
                    <Label>{t('users.username')} <span className="text-muted-foreground text-xs">({t('common.optional')})</span></Label>
                    <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="e.g. DC90" data-testid="user-username-input" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label>{editing ? t('users.newPassword') : t('users.oneTimePassword')} {!editing && '*'}</Label>
                  <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required={!editing} data-testid="user-password-input" />
                  {!editing && <p className="text-xs text-muted-foreground">{t('users.oneTimePasswordHint')}</p>}
                </div>
                <div className="space-y-2">
                  <Label>{t('users.role')} *</Label>
                  <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('users.allowedBranches')}</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto p-2 border border-border rounded-lg">
                    {branches.map(b => (
                      <div key={b.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={form.allowed_branch_ids.includes(b.id)}
                          onCheckedChange={() => toggleBranch(b.id)}
                          data-testid={`user-branch-${b.id}`}
                        />
                        <span className="text-sm text-muted-foreground">{b.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button type="submit" className="w-full bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="user-submit-btn">
                  {editing ? t('common.update') : t('common.create')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t('common.search')}
            value={fSearch}
            onChange={(e) => setFSearch(e.target.value)}
            className="ps-9"
            data-testid="users-search"
          />
          {fSearch && (
            <button onClick={() => setFSearch('')} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={fRole} onValueChange={setFRole}>
          <SelectTrigger className="w-full sm:w-[150px]" data-testid="users-role-filter"><SelectValue placeholder={t('users.role')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            {ROLES.map(r => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fBranch} onValueChange={setFBranch}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="users-branch-filter"><SelectValue placeholder={t('users.allowedBranches')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
            <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
          </Button>
        )}
      </div>

      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-0">
          <EnhancedTable stickyHeader>
            <StickyTableHeader>
              <tr className="border-b border-border">
                <SortableHeader field="name" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('common.name')}
                </SortableHeader>
                <SortableHeader field="email" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('users.email')}
                </SortableHeader>
                <SortableHeader field="role" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('users.role')}
                </SortableHeader>
                <SortableHeader field="created_at" currentField={sortField} currentDir={sortDir} onSort={toggleSort}>
                  {t('common.createdAt')}
                </SortableHeader>
                <TableHeader>{t('users.allowedBranches')}</TableHeader>
                <TableHeader align="end">{t('common.actions')}</TableHeader>
              </tr>
            </StickyTableHeader>
            <EnhancedTableBody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">{t('common.loading')}</td></tr>
              ) : filteredUsers.length === 0 ? (
                <TableEmptyState icon={UsersIcon} title={t('users.noUsers')} colSpan={6} />
              ) : (
                filteredUsers.map(u => (
                  <EnhancedTableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <EnhancedTableCell className="font-medium text-foreground">{u.name}</EnhancedTableCell>
                    <EnhancedTableCell className="text-muted-foreground">{u.email}</EnhancedTableCell>
                    <EnhancedTableCell>
                      <Badge variant="outline" className={roleStyles(u.role)}>
                        {roleLabel(u.role)}
                      </Badge>
                    </EnhancedTableCell>
                    <EnhancedTableCell className="text-xs text-muted-foreground">
                      {u.created_at ? formatDate(u.created_at) : '—'}
                    </EnhancedTableCell>
                    <EnhancedTableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                      {u.allowed_branch_ids?.map(id => branches.find(b => b.id === id)?.name || id).join(', ') || '—'}
                    </EnhancedTableCell>
                    <EnhancedTableCell align="end">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}>
                          <Pencil className="w-4 h-4 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setResetPwUser(u)} data-testid={`user-reset-pw-${u.id}`} title={t('users.resetPassword')}>
                          <Key className="w-4 h-4 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(u.id)} data-testid={`user-delete-${u.id}`}>
                          <Trash2 className="w-4 h-4 text-rose-400" />
                        </Button>
                      </div>
                    </EnhancedTableCell>
                  </EnhancedTableRow>
                ))
              )}
            </EnhancedTableBody>
          </EnhancedTable>
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <ResetPwDialog user={resetPwUser} onClose={() => setResetPwUser(null)} onSuccess={loadAll} />
    </div>
  );
}

function ResetPwDialog({ user: targetUser, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (!targetUser) return null;

  const handleReset = async () => {
    if (newPassword.length < 4) { toast.error(t('users.passwordTooShort')); return; }
    setLoading(true);
    try {
      await api.post(`/users/${targetUser.id}/reset-password`, { new_password: newPassword });
      toast.success(t('users.passwordResetSuccess', { name: targetUser.name }));
      setNewPassword('');
      onClose();
      onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || t('toast.error')); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!targetUser} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <DialogTitle className="font-barlow">{t('users.resetPassword')}</DialogTitle>
          <DialogDescription>{targetUser.name} ({targetUser.email})</DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-3">
          <Label>{t('users.oneTimePassword')}</Label>
          <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 4 characters" data-testid="users-reset-pw-input" />
          <p className="text-xs text-muted-foreground">{t('users.resetPasswordHint')}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleReset} disabled={loading || newPassword.length < 4} data-testid="users-confirm-reset-pw">{t('users.resetPassword')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
